import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/ChatGPT Image Aug 10, 2026, 06_50_42 PM.png';
import projectsData from '../projects_dataset.json';

const CATEGORIES = projectsData.metadata.categories || [];
const LEVELS = ['BE/B.Tech', 'M.Tech', 'PhD', 'Diploma', 'MCA', 'MBA', 'BSc'];
const ITEMS_PER_PAGE = 24;

export default function ProjectsListPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [level, setLevel] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredProjects = useMemo(() => {
    let filtered = projectsData.projects;

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(p => p.title.toLowerCase().includes(q));
    }

    if (category !== 'All') {
      filtered = filtered.filter(p => p.category === category);
    }

    if (level !== 'All') {
      filtered = filtered.filter(p => p.level === level);
    }

    return filtered;
  }, [search, category, level]);

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const currentProjects = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, category, level]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-ivory font-inter">
      {/* Page Header */}
      <div
        className="relative pt-24 pb-16 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #122266 50%, #0B1C4E 100%)' }}
      >
        <a href="/" className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-royal-gold transition-colors font-inter text-sm font-medium">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </a>

        <div className="flex items-center justify-center gap-3 mb-6">
          <img src={logo} alt="Connect Students" className="h-12 w-12 object-contain" />
          <div className="text-left">
            <p className="font-playfair font-bold text-white text-lg">Connect Students</p>
            <p className="font-inter text-royal-gold text-xs tracking-widest">LEARN • CONNECT • GROW</p>
          </div>
        </div>

        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 px-4">
          Explore{' '}
          <span style={{ background: 'linear-gradient(90deg, #C9A84C, #F0D080, #C9A84C)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmer 3s linear infinite' }}>
            {projectsData.projects.length.toLocaleString()}+ Projects
          </span>
        </h1>
        <p className="font-inter text-white/70 text-lg max-w-2xl mx-auto px-4">
          Browse our extensive database of academic projects across all domains and levels.
        </p>
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-parchment-dark p-6 mb-8 flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="font-inter text-xs font-semibold text-royal-navy mb-1.5 block">Search Projects</label>
            <div className="relative">
              <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-royal/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="e.g. Machine Learning, IoT..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-parchment border border-parchment-dark rounded-xl font-inter text-sm focus:outline-none focus:border-royal-gold focus:ring-1 focus:ring-royal-gold/20 transition-all"
              />
            </div>
          </div>
          <div className="w-full md:w-64 flex-shrink-0">
            <label className="font-inter text-xs font-semibold text-royal-navy mb-1.5 block">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-parchment-dark rounded-xl font-inter text-sm text-royal-navy focus:outline-none focus:border-royal-gold focus:ring-1 focus:ring-royal-gold/20 transition-all appearance-none cursor-pointer"
            >
              <option value="All">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="w-full md:w-48 flex-shrink-0">
            <label className="font-inter text-xs font-semibold text-royal-navy mb-1.5 block">Level</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-parchment-dark rounded-xl font-inter text-sm text-royal-navy focus:outline-none focus:border-royal-gold focus:ring-1 focus:ring-royal-gold/20 transition-all appearance-none cursor-pointer"
            >
              <option value="All">All Levels</option>
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Results Info */}
        <div className="mb-6 flex items-center justify-between">
          <p className="font-inter text-sm text-slate-royal/70">
            Showing <span className="font-bold text-royal-navy">{filteredProjects.length}</span> projects
          </p>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-parchment-dark">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="font-playfair text-2xl font-bold text-royal-navy mb-2">No Projects Found</h3>
            <p className="font-inter text-slate-royal/60 text-sm">Try adjusting your search or filters to see more results.</p>
            <button onClick={() => { setSearch(''); setCategory('All'); setLevel('All'); }} className="mt-6 font-inter font-semibold text-royal-navy border border-royal-navy/20 px-6 py-2.5 rounded-full hover:bg-royal-navy hover:text-white transition-colors text-sm">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence mode="popLayout">
              {currentProjects.map((proj, i) => (
                <motion.div
                  key={proj.title + i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white border-2 border-parchment-dark hover:border-royal-gold hover:shadow-gold rounded-2xl p-5 flex flex-col transition-all duration-300"
                >
                  <div className="flex-1">
                    <span className="inline-block px-2.5 py-1 bg-royal-navy/5 text-royal-navy font-semibold text-[10px] uppercase tracking-wider rounded-md mb-3">
                      {proj.category}
                    </span>
                    <h3 className="font-playfair font-bold text-royal-navy text-base leading-tight mb-3 line-clamp-3">
                      {proj.title}
                    </h3>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {proj.suggested_tech_stack?.slice(0, 4).map(tech => (
                        <span key={tech} className="bg-slate-50 border border-slate-200 text-slate-600 text-[10px] px-2 py-0.5 rounded-full">
                          {tech}
                        </span>
                      ))}
                      {proj.suggested_tech_stack?.length > 4 && (
                        <span className="bg-slate-50 border border-slate-200 text-slate-500 text-[10px] px-1.5 py-0.5 rounded-full">
                          +{proj.suggested_tech_stack.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-auto pt-4 border-t border-parchment-dark flex items-center justify-between">
                    <span className="font-inter text-xs text-slate-royal/60 font-medium">{proj.level}</span>
                    {proj.difficulty && (
                      <span className="font-inter text-[10px] font-bold text-royal-gold bg-royal-gold-pale px-2 py-1 rounded-md">
                        {proj.difficulty}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center rounded-xl border border-parchment-dark bg-white text-royal-navy disabled:opacity-40 hover:bg-parchment hover:border-royal-gold transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              
              {/* Show limited page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .map((p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${p}`}>
                        <span className="px-2 text-slate-royal/40">...</span>
                        <button
                          onClick={() => handlePageChange(p)}
                          className={`w-10 h-10 flex items-center justify-center rounded-xl border text-sm font-semibold transition-colors ${currentPage === p ? 'bg-royal-navy border-royal-navy text-white shadow-md' : 'border-parchment-dark bg-white text-royal-navy hover:bg-parchment hover:border-royal-gold'}`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl border text-sm font-semibold transition-colors ${currentPage === p ? 'bg-royal-navy border-royal-navy text-white shadow-md' : 'border-parchment-dark bg-white text-royal-navy hover:bg-parchment hover:border-royal-gold'}`}
                    >
                      {p}
                    </button>
                  );
                })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-xl border border-parchment-dark bg-white text-royal-navy disabled:opacity-40 hover:bg-parchment hover:border-royal-gold transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
